import {
  faArrowDown,
  faArrowUp,
  faCamera,
  faMinus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CameraPerformance } from 'domain/camera-performance'
import { Card } from 'primereact/card'
import React from 'react'
import './dashboard-camera-counter.scss'

export interface CameraCaptureCounterProps {
  cameraPerformance: CameraPerformance[]
  /** Mesma métrica, dia anterior — quando presente, mostra o comparativo
   *  "vs. ontem" em cada card. Casado por `cameraName`. */
  yesterdayCameraPerformance?: CameraPerformance[]
  /** Total geral, para quando `cameraPerformance` vem paginado (mobile).
   *  Sem isso, o total é a soma do próprio array recebido. */
  totalCaptures?: number
  /** Ex.: "2 de 3" — indicador de página, só usado no mobile paginado. */
  pageLabel?: string
}

type DeltaDirection = 'up' | 'down' | 'flat'

interface Delta {
  direction: DeltaDirection
  percent: number
  isNew: boolean
}

const computeDelta = (today: number, yesterday: number): Delta | null => {
  if (yesterday === 0) {
    return today === 0 ? null : { direction: 'up', percent: 0, isNew: true }
  }

  const percent = Math.round(((today - yesterday) / yesterday) * 100)
  return {
    direction: percent === 0 ? 'flat' : percent > 0 ? 'up' : 'down',
    percent,
    isNew: false,
  }
}

const deltaIcon = {
  up: faArrowUp,
  down: faArrowDown,
  flat: faMinus,
}

const CameraCaptureCounter: React.FC<CameraCaptureCounterProps> = ({
  cameraPerformance,
  yesterdayCameraPerformance,
  totalCaptures,
  pageLabel,
}) => {
  const total = (
    totalCaptures ??
    cameraPerformance.reduce((acc, camera) => acc + camera.eventCount, 0)
  ).toLocaleString('pt-BR')

  return (
    <>
      <div className='col-12'>
        <div className='vehicle-types-section'>
          <div className='section-header mb-4'>
            <h3 className='section-title'>
              <FontAwesomeIcon
                icon={faCamera}
                className='mr-2'
              />
              Detecções por Câmera
              {pageLabel && <span className='section-page-label'>{pageLabel}</span>}
            </h3>
            <div className='section-subtitle total-highlight'>
              <div className='total-label'>Total Detectado</div>
              <div className='total-number'>{total} veículos</div>
            </div>
          </div>
        </div>
      </div>
      <div className='grid'>
        {cameraPerformance.map((camera) => {
          const yesterdayCount = yesterdayCameraPerformance?.find(
            (y) => y.cameraName === camera.cameraName
          )?.eventCount

          const delta =
            yesterdayCameraPerformance !== undefined
              ? computeDelta(camera.eventCount, yesterdayCount ?? 0)
              : null

          return (
            <div key={camera.cameraName} className='col-6 sm:col-4 lg:col-4'>
              <Card className='camera-grid-card'>
                <div className='camera-grid-content'>
                  <div className='camera-header'>
                    <div className='camera-status-icon'>
                      <FontAwesomeIcon icon={faCamera} style={{ fontSize: '1rem' }} />
                    </div>
                  </div>
                  <div className='camera-info'>
                    <h4 className='camera-name'>{camera.cameraName}</h4>
                    <div className='camera-captures'>
                      {camera.eventCount.toLocaleString('pt-BR')}
                      <span className='captures-label'>capturas</span>
                    </div>
                    {delta && (
                      <div className={`camera-delta camera-delta--${delta.direction}`}>
                        <FontAwesomeIcon icon={deltaIcon[delta.direction]} />
                        {delta.isNew
                          ? 'novo hoje'
                          : `${delta.percent > 0 ? '+' : ''}${delta.percent}% vs ontem`}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default CameraCaptureCounter
